import { useId, useMemo, useRef } from "react";
import type { Currency, Locale } from "@nh/shared";
import { useFocusTrap } from "@/shared/lib/useFocusTrap";
import { ShopError } from "@/features/shop/components/ShopError";
import { useShopProducts } from "@/features/shop/useShopProducts";
import { usePriceViewer } from "@/features/shop/usePriceViewer";
import { useCart } from "../cart.context";
import { resolveCart, subtotalOf } from "../cart.pricing";
import { CartEmpty } from "./CartEmpty";
import { CartFooter } from "./CartFooter";
import { CartLineList } from "./CartLineList";
import { CartLinesSkeleton } from "./CartLinesSkeleton";
import { CartPanelHeader } from "./CartPanelHeader";

export interface CartDialogProps {
  currency: Currency;
  locale: Locale;
  onClose: () => void;
}

/**
 * El cajón del carrito. Solo se monta cuando está abierto.
 *
 * ── Por qué el catálogo se pide AQUÍ y no en el proveedor ────────────
 * `useShopProducts` es la misma consulta —y la misma clave de caché— que
 * usa la tienda, así que abrir el panel encima del catálogo no dispara
 * ninguna petición: reutiliza lo que ya está. Y como este componente solo
 * existe mientras el panel está abierto, en una ficha de producto el
 * catálogo no se pide hasta que alguien abre el carrito.
 *
 * Es también donde se cumple la promesa de `cart.model.ts`: el precio de
 * cada línea sale de estos datos recién traídos, no de `localStorage`. Si
 * el catálogo subió de precio, el carrito lo enseña sin más.
 *
 * ── Los estados de la petición se ven distintos ──────────────────────
 * Cargando → filas fantasma. Error → el mismo aviso con reintentar que la
 * tienda, porque es exactamente el mismo fallo. Vacío → una invitación a
 * volver al catálogo, nunca un panel en blanco.
 *
 * ── Accesibilidad del diálogo ────────────────────────────────────────
 * `useFocusTrap` hace tres cosas que un cajón sin ellas promete y no
 * cumple: mueve el foco dentro al abrir, lo hace dar la vuelta con Tab en
 * vez de escaparse a la página de debajo, y lo DEVUELVE al botón del
 * carrito al cerrar. Sin lo último, quien lo abrió con el teclado
 * aparecería al principio del documento.
 */
export function CartDialog({ currency, locale, onClose }: CartDialogProps) {
  const { lines, count, setQuantity, remove, clear } = useCart();
  const panelRef = useRef<HTMLDivElement>(null);
  const titleId = useId();

  useFocusTrap(panelRef, true, { onEscape: onClose });

  const { data, isPending, isError, error, refetch, isFetching } = useShopProducts();

  const products = useMemo(() => data?.items ?? [], [data]);
  const viewer = usePriceViewer();
  const entries = useMemo(
    () => resolveCart(lines, products, currency, viewer),
    [lines, products, currency, viewer],
  );
  const subtotal = useMemo(() => subtotalOf(entries, currency), [entries, currency]);

  const isEmpty = lines.length === 0;

  return (
    <div
      ref={panelRef}
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
      /*
       * `h-full` + `flex-col` con el cuerpo en `overflow-y-auto`: la
       * cabecera y el pie quedan fijos y solo se desplaza la lista. Con el
       * panel entero desplazable, el subtotal y el botón de comprar se van
       * fuera de la pantalla en cuanto hay cuatro artículos.
       *
       * `min-w-0` porque este panel es hijo de un flex, y un hijo de flex
       * trae `min-width: auto` — el ancho mínimo de su CONTENIDO, que pisa
       * al `w-full`. En una pantalla estrecha, un nombre de producto o un
       * precio largo podrían ensanchar el panel más allá del viewport y
       * dejar fuera la X de cerrar. Con esto, lo que se estrecha es el
       * contenido (que ya trae sus propios `min-w-0`), no el panel.
       */
      className="relative z-10 ml-auto flex h-full w-full min-w-0 max-w-md animate-apple-fade-in flex-col border-l border-ds-border bg-white shadow-ds-float"
    >
      <CartPanelHeader count={count} titleId={titleId} onClose={onClose} />

      {isEmpty ? (
        <CartEmpty onGoToShop={onClose} />
      ) : (
        <>
          <div className="min-h-0 flex-1 overflow-y-auto">
            {isPending ? (
              <CartLinesSkeleton count={lines.length} />
            ) : isError ? (
              <div className="px-5 py-4">
                <ShopError
                  error={error}
                  onRetry={() => void refetch()}
                  retrying={isFetching}
                />
              </div>
            ) : (
              <CartLineList
                entries={entries}
                locale={locale}
                onQuantityChange={setQuantity}
                onRemove={remove}
                onNavigate={onClose}
              />
            )}
          </div>

          <CartFooter subtotal={subtotal} locale={locale} onClear={clear} />
        </>
      )}
    </div>
  );
}
